import React from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Drawer,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import DownloadIcon from "@mui/icons-material/Download";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InventoryIcon from "@mui/icons-material/Inventory";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AnalyticsIcon from "@mui/icons-material/Insights";
import { ReportsAPI } from "../api/reports";
import { AnalyticsAPI } from "../api/analytics";
import { AssetsAPI } from "../api/assets";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { EnterprisesAPI } from "../api/enterprises";
import { ROUTES } from "../routes";
import jsQR from "jsqr";

function StatTile({ label, value, color = "#0f172a", onClick, selected = false }) {
  const help = {
    "Total Assets": "All assets included in the enterprise report scope.",
    Scanned: "Assets scanned during the active audit run.",
    Coverage: "Percentage of scoped assets covered by the current audit.",
    Exceptions: "Scans or assets that need admin review.",
    "Depreciable Assets": "Assets with financial data available for depreciation reporting.",
    "Fraud Signals": "Automated finance or audit risk indicators.",
    "Net Book Value": "Current remaining book value after depreciation.",
    "Gross Asset Value": "Original purchase value of the asset portfolio.",
    "Accumulated Depreciation": "Depreciation recorded so far across the portfolio.",
    "Duplicate Scans": "Repeated scan records that may need deduplication.",
    "Geo Breaches": "Assets scanned outside their configured geofence.",
    "Matched Devices": "Devices matched to the selected software family.",
    "Matched Installs": "Software installs found in the selected family.",
    "Managed Devices": "All managed devices included in the SAM baseline.",
    "Countable Devices": "Managed devices that matched a device-based family.",
    "Relevant Instances": "Licensable installs that should be counted toward demand.",
    "Families Covered": "Number of licensable families matched by the scan.",
    "Devices in Scope": "Managed devices covered by the SAM baseline.",
    "Inventory Rows": "Total inventory rows checked against the SAM baseline.",
    "Purchased Qty": "Quantity purchased by the customer from the PO or entitlement record.",
    "Installed Count": "Countable installs pulled from the current scan.",
    "License Gap": "Purchased quantity minus scan-relevant installed count.",
    "Reconciliation Status": "A quantity comparison only. License compliance requires entitlement and use-right validation.",
  }[label];

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        height: "100%",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-1px)",
              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
              borderColor: selected ? "#2563eb" : "#cbd5e1",
            }
          : undefined,
        ...(selected
          ? {
              borderColor: "#2563eb",
              boxShadow: "0 10px 24px rgba(37, 99, 235, 0.12)",
            }
          : null),
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" sx={{ color: "#64748b", letterSpacing: 0.25 }}>
          {label}
        </Typography>
        {help ? <InfoTip title={help} /> : null}
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, color, mt: 0.25 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function InfoTip({ title }) {
  return (
    <Tooltip title={title} arrow placement="top-start">
      <IconButton size="small" sx={{ ml: 0.5, mt: -0.2 }}>
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

function toSamNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getSamGapState(purchasedQty, installedCount) {
  const purchased = toSamNumber(purchasedQty);
  const installed = toSamNumber(installedCount);

  if (purchased == null || installed == null) {
    return { gap: null, status: "Pending", color: "default" };
  }

  const gap = purchased - installed;
  if (gap > 0) return { gap, status: "Surplus", color: "info" };
  if (gap < 0) return { gap, status: "Shortfall", color: "warning" };
  return { gap, status: "Balanced", color: "success" };
}

function buildGoogleMapsUrl(latitude, longitude) {
  if (latitude == null || longitude == null) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 1.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Stack>
  );
}

function ReportsUpgradeGate({ onUpgrade }) {
  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          border: "1px solid #dbeafe",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Chip
              label="Upgrade"
              variant="outlined"
              sx={{
                mb: 2,
                bgcolor: "#f8fafc",
                color: "#64748b",
                fontWeight: 700,
                letterSpacing: 0.4,
                borderColor: "#cbd5e1",
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15, mb: 1 }}>
              Unlock enterprise reporting
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
              This workspace is on the Free plan. Upgrade to enable audit history, depreciation, fraud signals, inventory intelligence, and finance-ready exports.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {[
              "Audit run summaries and review workflow",
              "Depreciation and net book value analysis",
              "Fraud signals, idle IT, and geofence insights",
              "CSV exports for monthly reconciliation",
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#fff",
                    border: "1px solid #e2e8f0",
                    minHeight: "100%",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
                    {item}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={onUpgrade}
              sx={{
                bgcolor: "#2563eb",
                color: "white",
                fontWeight: 800,
                textTransform: "none",
                px: 3,
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Upgrade to Enable Reports
            </Button>
            <Button
              variant="outlined"
              onClick={onUpgrade}
              sx={{
                borderColor: "#cbd5e1",
                color: "#475569",
                fontWeight: 700,
                textTransform: "none",
                px: 3,
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
              }}
            >
              View Pricing
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

function actionLabel(action) {
  const labels = {
    asset_created: "Asset Created",
    asset_updated: "Asset Updated",
    asset_custody_transferred: "Custody Transferred",
    asset_assigned: "Asset Assigned",
    asset_unassigned: "Asset Unassigned",
    asset_deleted: "Asset Deleted",
    asset_bulk_upload: "Bulk Upload",
    asset_bulk_deleted: "Bulk Delete",
  };
  return labels[action] || action;
}

function getTrailDetailValue(row, key) {
  if (!row) return null;
  return row?.details?.[key] ?? row?.[key] ?? null;
}

function formatTrailDetails(details) {
  if (!details) return "-";
  if (typeof details === "string") return details;
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
}

function formatTrailValue(value) {
  if (value == null || value === "") return "-";
  if (typeof value === "object") {
    const before = value.before ?? value.from ?? value.previous ?? null;
    const after = value.after ?? value.to ?? value.next ?? null;
    if (before != null || after != null) {
      return `${formatTrailValue(before)} → ${formatTrailValue(after)}`;
    }
    const parts = Object.values(value)
      .filter((entry) => entry != null && entry !== "")
      .map((entry) => formatTrailValue(entry));
    return parts.length ? parts.join(" • ") : "-";
  }
  return String(value);
}

function buildTrailHighlights(row) {
  const details = row?.details || {};
  const highlights = [];
  const changed = details?.changed && typeof details.changed === "object" ? details.changed : null;

  if (changed) {
    const keys = Object.keys(changed);
    if (keys.length) {
      highlights.push({
        label: "Changed fields",
        value: keys.map((key) => `${key}: ${formatTrailValue(changed[key])}`).join(" | "),
      });
    }
  }

  if (details?.from_asset_custodian || details?.to_asset_custodian) {
    highlights.push({
      label: "Custodian transfer",
      value: `${formatTrailValue(details.from_asset_custodian)} → ${formatTrailValue(details.to_asset_custodian)}`,
    });
  }

  if (details?.from_device_id != null || details?.to_device_id != null) {
    highlights.push({
      label: "Device assignment",
      value: `${formatTrailValue(details.from_device_hostname || details.from_device_id)} → ${formatTrailValue(details.to_device_hostname || details.to_device_id)}`,
    });
  }

  const simpleKeys = [
    ["asset_uid", "Asset UID"],
    ["owner_name", "Owner"],
    ["asset_type", "Asset type"],
    ["asset_status", "Status"],
    ["vendor_name", "Vendor"],
    ["location", "Location"],
    ["message", "Note"],
    ["reason", "Reason"],
    ["change_summary", "Summary"],
  ];
  for (const [key, label] of simpleKeys) {
    if (details[key]) {
      highlights.push({ label, value: formatTrailValue(details[key]) });
    }
  }

  return highlights;
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [enterpriseInfo, setEnterpriseInfo] = React.useState(null);
  const [enterpriseInfoLoading, setEnterpriseInfoLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [toast, setToast] = React.useState({ open: false, message: "", severity: "success" });

  const [history, setHistory] = React.useState([]);
  const [auditRuns, setAuditRuns] = React.useState([]);
  const [overview, setOverview] = React.useState(null);
  const [activeRun, setActiveRun] = React.useState(null);
  const [inventoryInsights, setInventoryInsights] = React.useState(null);
  const [depreciation, setDepreciation] = React.useState(null);
  const [fraudSignals, setFraudSignals] = React.useState(null);
  const [softwareAudit, setSoftwareAudit] = React.useState(null);
  const [auditScans, setAuditScans] = React.useState([]);
  const [auditScansBusy, setAuditScansBusy] = React.useState(false);

  const [startDialogOpen, setStartDialogOpen] = React.useState(false);
  const [scanDialogOpen, setScanDialogOpen] = React.useState(false);
  const [scanAssetUid, setScanAssetUid] = React.useState("");
  const [scanSource, setScanSource] = React.useState("manual");
  const [scanPhotos, setScanPhotos] = React.useState([]);
  const [scanLookupBusy, setScanLookupBusy] = React.useState(false);
  const [scannedAssetPreview, setScannedAssetPreview] = React.useState(null);
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const [scannerError, setScannerError] = React.useState("");
  const [reviewDialogOpen, setReviewDialogOpen] = React.useState(false);
  const [reviewScanId, setReviewScanId] = React.useState(null);
  const [reviewReason, setReviewReason] = React.useState("");
  const [reviewDialogMode, setReviewDialogMode] = React.useState("flag");
  const [geoRadiusMeters, setGeoRadiusMeters] = React.useState("100");
  const [startAuditLocating, setStartAuditLocating] = React.useState(false);
  const [startAuditLocationStatus, setStartAuditLocationStatus] = React.useState("idle");
  const [startAuditLocationMessage, setStartAuditLocationMessage] = React.useState("");

  const [displayCurrency] = React.useState("INR");
  const [reportTab, setReportTab] = React.useState(0);
  const [softwareSearch, setSoftwareSearch] = React.useState("");
  const [softwareCoverage, setSoftwareCoverage] = React.useState("ALL");
  const [softwareSort, setSoftwareSort] = React.useState("installations");
  const [softwarePage, setSoftwarePage] = React.useState(0);
  const [softwareRowsPerPage, setSoftwareRowsPerPage] = React.useState(10);
  const [softwareGapRows, setSoftwareGapRows] = React.useState([]);
  const [softwareDetailOpen, setSoftwareDetailOpen] = React.useState(false);
  const [selectedSoftwareRow, setSelectedSoftwareRow] = React.useState(null);
  const [auditHistorySearch, setAuditHistorySearch] = React.useState("");
  const [auditHistoryStatus, setAuditHistoryStatus] = React.useState("ALL");
  const [auditHistoryPage, setAuditHistoryPage] = React.useState(0);
  const [auditHistoryRowsPerPage, setAuditHistoryRowsPerPage] = React.useState(10);
  const [assetTrailSearch, setAssetTrailSearch] = React.useState("");
  const [assetTrailAction, setAssetTrailAction] = React.useState("ALL");
  const [assetTrailPage, setAssetTrailPage] = React.useState(0);
  const [assetTrailRowsPerPage, setAssetTrailRowsPerPage] = React.useState(10);
  const [selectedTrailRow, setSelectedTrailRow] = React.useState(null);
  const [financeSearch, setFinanceSearch] = React.useState("");
  const [financeAssetType, setFinanceAssetType] = React.useState("ALL");
  const [fraudSeverity, setFraudSeverity] = React.useState("ALL");
  const [fraudTableOpen, setFraudTableOpen] = React.useState(false);
  const [depPage, setDepPage] = React.useState(0);
  const [depRowsPerPage, setDepRowsPerPage] = React.useState(10);
  const [fraudPage, setFraudPage] = React.useState(0);
  const [fraudRowsPerPage, setFraudRowsPerPage] = React.useState(10);
  const [auditRunSort, setAuditRunSort] = React.useState({ by: "started_at", dir: "desc" });
  const [assetTrailSort, setAssetTrailSort] = React.useState({ by: "created_at", dir: "desc" });
  const [depreciationSort, setDepreciationSort] = React.useState({ by: "purchase_price", dir: "desc" });

  const toggleTableSort = (setSort, by) => {
    setSort((current) => ({ by, dir: current.by === by && current.dir === "asc" ? "desc" : "asc" }));
  };

  const videoRef = React.useRef(null);
  const qrCanvasRef = React.useRef(null);
  const mediaStreamRef = React.useRef(null);
  const detectRafRef = React.useRef(null);
  const detectLockRef = React.useRef(false);

  const currentMonth = React.useMemo(() => new Date().toISOString().slice(0, 7), []);
  const reportGeneratedAt = React.useMemo(() => new Date(), []);
  const reportPacketTitle = "Full Audit Report";

  React.useEffect(() => {
    let mounted = true;
    const role = auth?.user?.role;
    if (role !== "enterprise_admin") {
      setEnterpriseInfoLoading(false);
      setEnterpriseInfo(null);
      return undefined;
    }

    setEnterpriseInfoLoading(true);
    EnterprisesAPI.getMyEnterprise()
      .then((res) => {
        if (!mounted) return;
        setEnterpriseInfo(res?.data?.data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setEnterpriseInfo(null);
      })
      .finally(() => {
        if (mounted) setEnterpriseInfoLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [auth?.user?.role]);

  const showToast = React.useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const currentTier = String(enterpriseInfo?.user_status || "").toLowerCase();
  const reportsLocked = !enterpriseInfoLoading && currentTier === "free";
  const handleUpgrade = React.useCallback(() => {
    navigate(ROUTES.PRICING);
  }, [navigate]);

  const handleDownloadSamWorkbook = React.useCallback(async () => {
    try {
      const response = await AnalyticsAPI.softwareWorkbook();
      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `sam_reference_workbook_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      showToast("SAM workbook downloaded.", "success");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to download SAM workbook");
    }
  }, [showToast]);

  const moneyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: displayCurrency,
        maximumFractionDigits: 2,
      }),
    [displayCurrency]
  );

  const formatMoney = React.useCallback(
    (value) => {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return "-";
      return moneyFormatter.format(n);
    },
    [moneyFormatter]
  );

  const loadAll = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [h, runs, ov, active, inv, dep, fraud, samAudit] = await Promise.all([
        ReportsAPI.assetHistory({ limit: 50 }),
        ReportsAPI.listAuditRuns({ limit: 20 }),
        ReportsAPI.auditOverview(),
        ReportsAPI.getActiveAuditRun(),
        ReportsAPI.inventoryInsights({ days: 30 }),
        ReportsAPI.financeDepreciation(),
        ReportsAPI.financeFraudSignals(),
        AnalyticsAPI.softwareFamilies(),
      ]);

      setHistory(h?.data?.data || []);
      setAuditRuns(runs?.data?.data || []);
      setOverview(ov?.data?.data || null);
      setActiveRun(active?.data?.data || null);
      setInventoryInsights(inv?.data?.data || null);
      setDepreciation(dep?.data?.data || null);
      setFraudSignals(fraud?.data?.data || null);
      setSoftwareAudit(samAudit?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load reports");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchScannedAssetPreview = React.useCallback(async (uid) => {
    const safeUid = String(uid || "").trim();
    if (!safeUid) return null;
    setScanLookupBusy(true);
    try {
      const response = await AssetsAPI.publicDetails(safeUid);
      const data = response?.data?.data || null;
      setScannedAssetPreview(data);
      return data;
    } catch (err) {
      setScannedAssetPreview(null);
      throw new Error(err?.response?.data?.error || "Asset details not found for scanned code");
    } finally {
      setScanLookupBusy(false);
    }
  }, []);

  const getCurrentLocation = React.useCallback(async () => {
    if (!navigator?.geolocation) {
      throw new Error("Geolocation is not supported by this browser.");
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: Number(pos.coords.latitude.toFixed(7)),
            longitude: Number(pos.coords.longitude.toFixed(7)),
          });
        },
        (err) => {
          if (err?.code === 1) {
            reject(new Error("Location permission denied. Please allow location access and try again."));
            return;
          }
          if (err?.code === 2) {
            reject(new Error("Unable to determine current location."));
            return;
          }
          if (err?.code === 3) {
            reject(new Error("Location request timed out. Please retry."));
            return;
          }
          reject(new Error("Could not read current location."));
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
      );
    });
  }, []);

  const captureStartAuditLocation = React.useCallback(async () => {
    setStartAuditLocating(true);
    setStartAuditLocationStatus("locating");
    setStartAuditLocationMessage("");
    try {
      await getCurrentLocation();
      setStartAuditLocationStatus("ready");
      setStartAuditLocationMessage("Current location captured successfully.");
    } finally {
      setStartAuditLocating(false);
    }
  }, [getCurrentLocation]);

  const stopScanner = React.useCallback(() => {
    if (detectRafRef.current) {
      cancelAnimationFrame(detectRafRef.current);
      detectRafRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    detectLockRef.current = false;
    setScannerOpen(false);
  }, []);

  React.useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  React.useEffect(() => {
    if (!scanDialogOpen) {
      stopScanner();
      setScannerError("");
      setScannedAssetPreview(null);
    }
  }, [scanDialogOpen, stopScanner]);

  React.useEffect(() => {
    if (startDialogOpen) {
      void captureStartAuditLocation().catch((err) => {
        setStartAuditLocationStatus("error");
        setStartAuditLocationMessage(err?.message || "Unable to capture current location.");
      });
      return;
    }
    setStartAuditLocating(false);
    setStartAuditLocationStatus("idle");
    setStartAuditLocationMessage("");
  }, [startDialogOpen, captureStartAuditLocation]);

  React.useEffect(() => {
    if (enterpriseInfoLoading) return;
    if (reportsLocked) {
      setLoading(false);
      return;
    }
    void loadAll();
  }, [enterpriseInfoLoading, loadAll, reportsLocked]);

  const loadAuditScans = React.useCallback(async (runId) => {
    if (!runId) {
      setAuditScans([]);
      return;
    }
    setAuditScansBusy(true);
    try {
      const res = await ReportsAPI.listAuditRunScans(runId);
      setAuditScans(res?.data?.data || []);
    } catch {
      setAuditScans([]);
    } finally {
      setAuditScansBusy(false);
    }
  }, []);

  React.useEffect(() => {
    void loadAuditScans(activeRun?.id || null);
  }, [activeRun?.id, loadAuditScans]);

  const refreshDashboard = React.useCallback(async () => {
    setBusy(true);
    try {
      await loadAll({ silent: false });
      await loadAuditScans(activeRun?.id || null);
      showToast("Dashboard refreshed.", "success");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to refresh dashboard");
    } finally {
      setBusy(false);
    }
  }, [activeRun?.id, loadAll, loadAuditScans, showToast]);

  const runSummary = activeRun?.summary || overview?.latest_run_summary || {
    tagged_assets: overview?.tagged_assets || 0,
    scanned_assets: 0,
    coverage_pct: 0,
    exceptions: 0,
    geofence_breaches: 0,
  };

  const startAudit = async () => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const location = await getCurrentLocation();
      await ReportsAPI.startAuditRun({
        geofence_center_latitude: location.latitude,
        geofence_center_longitude: location.longitude,
        geofence_radius_meters: geoRadiusMeters || null,
      });
      setStartDialogOpen(false);
      setSuccess("Asset audit started.");
      showToast("Asset audit started successfully.", "success");
      await loadAll({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to start audit");
    } finally {
      setBusy(false);
    }
  };

  const openResolveReview = async (scanId) => {
    if (!scanId) return;
    setReviewScanId(scanId);
    setReviewDialogMode("resolve");
    setReviewReason("");
    setReviewDialogOpen(true);
  };

  const submitReviewReason = async () => {
    if (!reviewScanId) return;
    setBusy(true);
    try {
      if (reviewDialogMode === "resolve") {
        await ReportsAPI.approveSelfScan(reviewScanId);
        showToast("Review resolved", "success");
      } else {
        const reason = String(reviewReason || "").trim();
        if (!reason) {
          showToast("Review note is required.", "warning");
          return;
        }
        await ReportsAPI.sendSelfScanForReview(reviewScanId, { review_reason: reason });
        showToast("Scan flagged for review", "success");
      }
      setReviewDialogOpen(false);
      setReviewScanId(null);
      setReviewReason("");
      setReviewDialogMode("flag");
      await loadAuditScans(activeRun?.id);
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to update scan review state", "error");
    } finally {
      setBusy(false);
    }
  };

  const completeAudit = async () => {
    if (!activeRun?.id) return;
    setBusy(true);
    setError("");
    try {
      await ReportsAPI.completeAuditRun(activeRun.id);
      setSuccess("Audit run completed.");
      showToast("Audit run completed.", "success");
      await loadAll({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to complete audit");
    } finally {
      setBusy(false);
    }
  };

  const addScan = React.useCallback(async ({
    assetUid = scanAssetUid,
    source = scanSource,
    closeDialog = true,
  } = {}) => {
    const safeAssetUid = String(assetUid || "").trim();
    if (!activeRun?.id || !safeAssetUid) return;
    setBusy(true);
    setError("");
    try {
      // Location is mandatory for enterprise audit traceability.
      const location = await getCurrentLocation();
      const scanPayload = scanPhotos.length
        ? (() => {
            const formData = new FormData();
            formData.append("asset_uid", safeAssetUid);
            formData.append("latitude", location.latitude);
            formData.append("longitude", location.longitude);
            formData.append("scanned_at", new Date().toISOString());
            formData.append("source", source);
            scanPhotos.slice(0, 3).forEach((file) => formData.append("images", file));
            return formData;
          })()
        : {
            asset_uid: safeAssetUid,
            latitude: location.latitude,
            longitude: location.longitude,
            scanned_at: new Date().toISOString(),
            source,
          };

      const resp = await ReportsAPI.addAuditScan(activeRun.id, scanPayload);
      const movement = resp?.data?.data?.movement_alert;
      if (closeDialog) {
        setScanDialogOpen(false);
        setScanAssetUid("");
        setScannedAssetPreview(null);
        setScanPhotos([]);
      }
      setSuccess(
        movement
          ? `Scan saved and movement alert generated for ${safeAssetUid}.`
          : `Scan saved for ${safeAssetUid}.`
      );
      showToast(movement ? "Movement alert triggered for this scan." : "Scan saved successfully.", movement ? "warning" : "success");
      await loadAll({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save scan");
    } finally {
      setBusy(false);
    }
  }, [activeRun?.id, getCurrentLocation, loadAll, scanAssetUid, scanPhotos, scanSource, showToast]);

  const processDetectedCode = React.useCallback(async (rawValue, source = scanSource) => {
    const normalized = String(rawValue || "").trim();
    if (!normalized) return;
    setScanAssetUid(normalized);
    try {
      await fetchScannedAssetPreview(normalized);
      await addScan({ assetUid: normalized, source, closeDialog: false });
      showToast("Code scanned, asset identified, and scan saved.", "success");
    } catch (err) {
      setError(err?.message || "Unable to process scanned code");
    }
  }, [addScan, fetchScannedAssetPreview, scanSource, showToast]);

  const openCameraScanner = React.useCallback(async () => {
    setScannerError("");
    try {
      if (!window.isSecureContext) {
        setScannerError("Camera access requires HTTPS or localhost. Open the app on localhost/HTTPS and try again.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      mediaStreamRef.current = stream;

      setScannerOpen(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const detectFrame = async () => {
        if (!videoRef.current || detectLockRef.current) {
          detectRafRef.current = requestAnimationFrame(detectFrame);
          return;
        }
        try {
          const video = videoRef.current;
          const width = video.videoWidth;
          const height = video.videoHeight;
          if (width > 0 && height > 0) {
            if (!qrCanvasRef.current) {
              qrCanvasRef.current = document.createElement("canvas");
            }
            const canvas = qrCanvasRef.current;
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, 0, 0, width, height);
              const imageData = ctx.getImageData(0, 0, width, height);
              const code = jsQR(imageData.data, width, height, { inversionAttempts: "attemptBoth" });
              const rawValue = String(code?.data || "").trim();
              if (rawValue) {
                detectLockRef.current = true;
                stopScanner();
                await processDetectedCode(rawValue, "camera_qr");
                return;
              }
            }
          }
        } catch {
          setScannerError("Unable to decode QR right now. Try better lighting or hold camera steady.");
        }
        detectRafRef.current = requestAnimationFrame(detectFrame);
      };

      detectRafRef.current = requestAnimationFrame(detectFrame);
    } catch (err) {
      stopScanner();
      setScannerError(err?.message || "Could not open camera scanner");
    }
  }, [processDetectedCode, stopScanner]);

  const downloadFullReport = React.useCallback(async ({ manageBusy = true, silentToast = false, runId = null } = {}) => {
    if (manageBusy) setBusy(true);
    setError("");
    try {
      const reportMonth = currentMonth;
      const [
        assetsRes,
        historyRes,
        runsRes,
        overviewRes,
        activeRes,
        inventoryRes,
        depRes,
        fraudRes,
      ] = await Promise.all([
        AssetsAPI.list(),
        ReportsAPI.assetHistory({ limit: 200 }),
        ReportsAPI.listAuditRuns({ limit: 50 }),
        ReportsAPI.auditOverview(),
        ReportsAPI.getActiveAuditRun(),
        ReportsAPI.inventoryInsights({ days: 30 }),
        ReportsAPI.financeDepreciation(),
        ReportsAPI.financeFraudSignals(),
      ]);

      const allRuns = runsRes?.data?.data || [];
      const targetRunId = runId || activeRes?.data?.data?.id || allRuns[0]?.id || null;

      const runScanMap = new Map();
      if (targetRunId) {
        try {
          const runCsvRes = await ReportsAPI.downloadAuditRunCsv(targetRunId);
          const csvText = await new Response(runCsvRes.data).text();

          const parseCsvLine = (line) => {
            const out = [];
            let current = "";
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
              const ch = line[i];
              if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') {
                  current += '"';
                  i += 1;
                } else {
                  inQuotes = !inQuotes;
                }
              } else if (ch === "," && !inQuotes) {
                out.push(current);
                current = "";
              } else {
                current += ch;
              }
            }
            out.push(current);
            return out;
          };

          const lines = String(csvText || "").split(/\r?\n/).filter((l) => l.trim());
          if (lines.length > 1) {
            const headers = parseCsvLine(lines[0]);
            for (let i = 1; i < lines.length; i++) {
              const cells = parseCsvLine(lines[i]);
              const row = {};
              headers.forEach((h, idx) => {
                row[h] = cells[idx] ?? "";
              });
              const uid = String(row.asset_uid || "").trim();
              if (uid) runScanMap.set(uid, row);
            }
          }
        } catch {
          // Continue generating report even if per-run scan CSV is unavailable.
        }
      }

      const report = {
        generated_at: new Date().toISOString(),
        filters: {},
        run_context: {
          requested_run_id: runId,
          active_run: activeRes?.data?.data || null,
          selected_run: allRuns.find((r) => String(r.id) === String(targetRunId || "")) || null,
        },
        assets: assetsRes?.data?.data || [],
        overview: overviewRes?.data?.data || null,
        inventory_audit: inventoryRes?.data?.data || null,
        finance_audit: {
          depreciation: depRes?.data?.data || null,
          fraud_signals: fraudRes?.data?.data || null,
        },
        history: {
          asset_change_events: historyRes?.data?.data || [],
          audit_runs: runsRes?.data?.data || [],
        },
      };

      const esc = (v) => {
        const s = v == null ? "" : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      };

      const depMap = new Map((report.finance_audit?.depreciation?.assets || []).map((d) => [String(d.asset_uid), d]));
      const fraudMap = new Map();
      for (const s of report.finance_audit?.fraud_signals?.signals || []) {
        const uid = String(s.asset_uid || "");
        if (!uid) continue;
        const rec = fraudMap.get(uid) || { total: 0, high: 0, types: new Set() };
        rec.total += 1;
        if (String(s.severity || "").toLowerCase() === "high") rec.high += 1;
        rec.types.add(String(s.type || "unknown"));
        fraudMap.set(uid, rec);
      }
      const movementMap = new Map();
      for (const ev of report.inventory_audit?.recent_movement_events || []) {
        const uid = String(ev?.details?.asset_uid || "");
        if (!uid || movementMap.has(uid)) continue;
        movementMap.set(uid, ev);
      }

      const nonTaggableTypes = new Set(["LICENSE", "SOFTWARE"]);
      const assets = [...(report.assets || [])].sort((a, b) => String(a.asset_uid || "").localeCompare(String(b.asset_uid || "")));

      const lines = [];
      lines.push([
        "report_month",
        "generated_at",
        "audit_run_id",
        "audit_run_status",
        "asset_uid",
        "asset_type",
        "asset_status",
        "owner_name",
        "asset_custodian",
        "asset_custodian_email",
        "asset_custodian_contact",
        "department",
        "location",
        "vendor_name",
        "purchase_date",
        "is_taggable_asset",
        "scan_status",
        "scanned_in_selected_run",
        "scan_time",
        "scan_source",
        "scan_latitude",
        "scan_longitude",
        "is_geofence_breach",
        "distance_from_geofence_meters",
        "movement_alert_recent",
        "movement_distance_recent_meters",
        "purchase_price",
        "depreciation_method",
        "useful_life_months",
        "salvage_value",
        "depreciation_rate_percent",
        "depreciation_start_mode",
        "months_used",
        "monthly_depreciation",
        "accumulated_depreciation",
        "net_book_value",
        "fraud_signal_count",
        "fraud_high_count",
        "fraud_types",
        "last_scanned_at",
      ].map(esc).join(","));

      for (const asset of assets) {
        const uid = String(asset.asset_uid || "");
        const scan = runScanMap.get(uid);
        const dep = depMap.get(uid);
        const fraud = fraudMap.get(uid);
        const move = movementMap.get(uid);
        const scanStatus = scan?.scan_status || (asset.last_scanned_at ? "SCANNED" : "NOT_SCANNED");

        lines.push([
          esc(reportMonth),
          esc(report.generated_at),
          esc(targetRunId),
          esc(report.run_context.selected_run?.status || report.run_context.active_run?.status || ""),
          esc(uid),
          esc(asset.asset_type),
          esc(asset.asset_status),
          esc(asset.owner_name),
          esc(asset.asset_custodian),
          esc(asset.asset_custodian_email),
          esc(asset.asset_custodian_contact),
          esc(asset.department),
          esc(asset.location),
          esc(asset.vendor_name),
          esc(asset.purchase_date),
          esc(!nonTaggableTypes.has(String(asset.asset_type || "").toUpperCase()) ? "YES" : "NO"),
          esc(scanStatus),
          esc(scan ? (scanStatus === "SCANNED" ? "YES" : "NO") : ""),
          esc(scan?.scanned_at || ""),
          esc(scan?.source || move?.details?.source || ""),
          esc(scan?.latitude || ""),
          esc(scan?.longitude || ""),
          esc(scan?.is_geofence_breach || ""),
          esc(scan?.distance_from_geofence_meters || ""),
          esc(move ? "YES" : "NO"),
          esc(move?.details?.movement_distance_meters || ""),
          esc(dep?.purchase_price ?? asset.purchase_price ?? ""),
          esc(dep?.depreciation_method || ""),
          esc(dep?.useful_life_months ?? dep?.useful_life_years ?? ""),
          esc(dep?.salvage_value ?? ""),
          esc(dep?.depreciation_rate_percent ?? ""),
          esc(dep?.depreciation_start_mode || ""),
          esc(dep?.months_used || ""),
          esc(dep?.monthly_depreciation || ""),
          esc(dep?.accumulated_depreciation || ""),
          esc(dep?.net_book_value || ""),
          esc(fraud?.total || 0),
          esc(fraud?.high || 0),
          esc(fraud ? Array.from(fraud.types).join("|") : ""),
          esc(asset.last_scanned_at || ""),
        ].join(","));
      }

      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asset_audit_report_${reportMonth}${targetRunId ? `_run_${targetRunId}` : ""}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (!silentToast) showToast("Full audit report downloaded.", "success");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to download full report");
    } finally {
      if (manageBusy) setBusy(false);
    }
  }, [currentMonth, showToast]);

  const fetchManualAssetDetails = async () => {
    if (!scanAssetUid.trim()) return;
    try {
      await fetchScannedAssetPreview(scanAssetUid);
      showToast("Asset details loaded.", "success");
    } catch (err) {
      setError(err?.message || "Unable to fetch asset details");
    }
  };

  const runScanAll = async () => {
    if (!activeRun?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await ReportsAPI.scanAllTagged(activeRun.id);
      const d = res?.data?.data;
      setSuccess(`Scan all completed. Added ${d?.created || 0} and skipped ${d?.skipped || 0}.`);
      showToast("Scan all tagged assets completed.", "success");
      await loadAll({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to scan all tagged assets");
    } finally {
      setBusy(false);
    }
  };

  const depreciationRows = depreciation?.assets || [];
  const softwareRows = React.useMemo(() => softwareAudit?.families || [], [softwareAudit]);
  React.useEffect(() => {
    setSoftwareGapRows((prevRows) => {
      const previousByKey = new Map(prevRows.map((row) => [row.key, row]));
      return softwareRows.map((row) => {
        const key = String(row.key || row.label || row.name || "").trim();
        const previous = previousByKey.get(key);
        return {
          key,
          vendor: row.vendor || "-",
          sku: row.label || row.name || key || "-",
          canonical_skus: row.preferred_skus || [],
          purchased_qty: previous?.purchased_qty ?? "",
          po_number: previous?.po_number ?? "",
          po_date: previous?.po_date ?? "",
          installed_count: Number(row.countable_instances ?? row.matched_installations ?? 0),
          notes: previous?.notes ?? row.notes ?? "",
        };
      });
    });
  }, [softwareRows]);

  const updateSamGapRow = React.useCallback((rowKey, field, value) => {
    setSoftwareGapRows((prevRows) =>
      prevRows.map((row) =>
        row.key === rowKey
          ? { ...row, [field]: value }
          : row
      )
    );
  }, []);
  const softwareFilteredRows = React.useMemo(() => {
    const query = String(softwareSearch || "").trim().toLowerCase();
    const coverage = String(softwareCoverage || "ALL").toLowerCase();
    return softwareRows.filter((row) => {
      const matchesQuery =
        !query ||
        String(row.label || "").toLowerCase().includes(query) ||
        String(row.key || "").toLowerCase().includes(query) ||
        (row.sample_products || []).some((product) => String(product || "").toLowerCase().includes(query)) ||
        (row.sample_devices || []).some((device) => String(device?.hostname || device?.os || "").toLowerCase().includes(query));
      const matchesCoverage = coverage === "all" || String(row.counting_basis || "").toLowerCase() === coverage;
      return matchesQuery && matchesCoverage;
    });
  }, [softwareCoverage, softwareRows, softwareSearch]);
  const softwareSortedRows = React.useMemo(() => {
    const rows = [...softwareFilteredRows];
    return rows.sort((left, right) => {
      if (softwareSort === "name") return String(left.label || "").localeCompare(String(right.label || ""));
      if (softwareSort === "devices") return (right.matched_devices || 0) - (left.matched_devices || 0);
      return (right.countable_instances || 0) - (left.countable_instances || 0);
    });
  }, [softwareFilteredRows, softwareSort]);
  const pagedSoftwareRows = softwareSortedRows.slice(
    softwarePage * softwareRowsPerPage,
    softwarePage * softwareRowsPerPage + softwareRowsPerPage
  );
  const auditRunRows = React.useMemo(() => {
    return [...auditRuns].sort((left, right) => {
      const valueFor = (row) => {
        if (auditRunSort.by === "id") return Number(row?.id || 0);
        if (auditRunSort.by === "started_at") return new Date(row?.started_at || row?.created_at || 0).getTime();
        if (["coverage_pct", "scanned_assets", "exceptions"].includes(auditRunSort.by)) return Number(row?.summary?.[auditRunSort.by] || 0);
        return String(row?.[auditRunSort.by] || "");
      };
      const a = valueFor(left); const b = valueFor(right);
      const direction = auditRunSort.dir === "asc" ? 1 : -1;
      return typeof a === "number" && typeof b === "number"
        ? (a - b) * direction
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }) * direction;
    });
  }, [auditRuns, auditRunSort]);
  const filteredAuditRunRows = React.useMemo(() => {
    const query = String(auditHistorySearch || "").trim().toLowerCase();
    const status = String(auditHistoryStatus || "ALL").toLowerCase();
    return auditRunRows.filter((row) => {
      const rowStatus = String(row?.status || "").toLowerCase();
      const summary = row?.summary || {};
      const matchesQuery =
        !query ||
        String(row?.id || "").toLowerCase().includes(query) ||
        rowStatus.includes(query) ||
        String(summary?.coverage_pct ?? "").toLowerCase().includes(query) ||
        String(summary?.scanned_assets ?? "").toLowerCase().includes(query) ||
        String(summary?.exceptions ?? "").toLowerCase().includes(query);
      const matchesStatus = status === "all" || rowStatus === status;
      return matchesQuery && matchesStatus;
    });
  }, [auditHistorySearch, auditHistoryStatus, auditRunRows]);
  const pagedAuditRunRows = filteredAuditRunRows.slice(
    auditHistoryPage * auditHistoryRowsPerPage,
    auditHistoryPage * auditHistoryRowsPerPage + auditHistoryRowsPerPage
  );
  const auditTrailRows = React.useMemo(() => {
    return [...history].sort((left, right) => {
      const valueFor = (row) => {
        if (assetTrailSort.by === "created_at") return new Date(row?.created_at || 0).getTime();
        if (assetTrailSort.by === "asset_uid") return getTrailDetailValue(row, "asset_uid") || getTrailDetailValue(row, "asset_id") || "";
        if (assetTrailSort.by === "actor") return row?.actor?.email || "system";
        return row?.[assetTrailSort.by] || "";
      };
      const a = valueFor(left); const b = valueFor(right);
      const direction = assetTrailSort.dir === "asc" ? 1 : -1;
      return typeof a === "number" && typeof b === "number"
        ? (a - b) * direction
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }) * direction;
    });
  }, [assetTrailSort, history]);
  const filteredAuditTrailRows = React.useMemo(() => {
    const query = String(assetTrailSearch || "").trim().toLowerCase();
    const actionFilter = String(assetTrailAction || "ALL").toLowerCase();
    return auditTrailRows.filter((row) => {
      const rowAction = String(row?.action || "").toLowerCase();
      const actorEmail = String(row?.actor?.email || "system").toLowerCase();
      const detailsText = formatTrailDetails(row?.details).toLowerCase();
      const matchesQuery =
        !query ||
        String(row?.id || "").toLowerCase().includes(query) ||
        rowAction.includes(query) ||
        actorEmail.includes(query) ||
        detailsText.includes(query);
      const matchesAction = actionFilter === "all" || rowAction === actionFilter;
      return matchesQuery && matchesAction;
    });
  }, [assetTrailAction, assetTrailSearch, auditTrailRows]);
  const pagedAuditTrailRows = filteredAuditTrailRows.slice(
    assetTrailPage * assetTrailRowsPerPage,
    assetTrailPage * assetTrailRowsPerPage + assetTrailRowsPerPage
  );
  const samGapSummary = React.useMemo(() => {
    return softwareGapRows.reduce(
      (acc, row) => {
        const state = getSamGapState(row.purchased_qty, row.installed_count);
        if (state.status === "Shortfall") acc.shortfall += 1;
        else if (state.status === "Surplus") acc.surplus += 1;
        else if (state.status === "Balanced") acc.balanced += 1;
        else acc.pending += 1;
        return acc;
      },
      { shortfall: 0, surplus: 0, balanced: 0, pending: 0 }
    );
  }, [softwareGapRows]);
  const filteredDepreciationRows = React.useMemo(() => depreciationRows.filter((row) => {
    const query = String(financeSearch || "").trim().toLowerCase();
    const matchesQuery =
      !query ||
      String(row.asset_uid || "").toLowerCase().includes(query) ||
      String(row.asset_type || "").toLowerCase().includes(query);
    const matchesType = financeAssetType === "ALL" || String(row.asset_type || "").toUpperCase() === financeAssetType;
    return matchesQuery && matchesType;
  }).sort((left, right) => {
    const valueFor = (row) => {
      if (depreciationSort.by === "useful_life_months") return Number(row?.useful_life_months ?? row?.useful_life_years ?? 0);
      if (["purchase_price", "salvage_value", "months_used", "monthly_depreciation", "accumulated_depreciation", "net_book_value"].includes(depreciationSort.by)) return Number(row?.[depreciationSort.by] ?? 0);
      return String(row?.[depreciationSort.by] || "");
    };
    const a = valueFor(left); const b = valueFor(right);
    const direction = depreciationSort.dir === "asc" ? 1 : -1;
    return typeof a === "number" && typeof b === "number"
      ? (a - b) * direction
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }) * direction;
  }), [depreciationRows, depreciationSort, financeAssetType, financeSearch]);
  const pagedDepreciationRows = filteredDepreciationRows.slice(
    depPage * depRowsPerPage,
    depPage * depRowsPerPage + depRowsPerPage
  );
  const fraudRows = React.useMemo(() => fraudSignals?.signals || [], [fraudSignals]);
  const normalizedFraudRows = React.useMemo(() => {
    const severityRank = { high: 0, medium: 1, low: 2 };
    return [...fraudRows]
      .map((row, index) => ({
        id: `${row.asset_uid || "signal"}-${row.type || "fraud"}-${index}`,
        severity: String(row.severity || "medium").toLowerCase(),
        asset_uid: row.asset_uid || "-",
        type: row.type || "-",
        message: row.message || "-",
      }))
      .sort((left, right) => {
        const leftRank = severityRank[left.severity] ?? 3;
        const rightRank = severityRank[right.severity] ?? 3;
        if (leftRank !== rightRank) return leftRank - rightRank;
        return String(left.asset_uid).localeCompare(String(right.asset_uid));
      });
  }, [fraudRows]);
  const filteredFraudRows = normalizedFraudRows.filter((row) => {
    const query = String(financeSearch || "").trim().toLowerCase();
    const matchesQuery =
      !query ||
      String(row.asset_uid || "").toLowerCase().includes(query) ||
      String(row.type || "").toLowerCase().includes(query) ||
      String(row.message || "").toLowerCase().includes(query);
    const matchesSeverity =
      fraudSeverity === "ALL" ||
      String(row.severity || "").toLowerCase() === String(fraudSeverity || "").toLowerCase();
    return matchesQuery && matchesSeverity;
  });
  const pagedFraudRows = filteredFraudRows.slice(
    fraudPage * fraudRowsPerPage,
    fraudPage * fraudRowsPerPage + fraudRowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return enterpriseInfoLoading ? (
    <Box sx={{ minHeight: "72vh", display: "grid", placeItems: "center", px: 2 }}>
      <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 520, width: "100%", textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>
          Loading report access
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verifying your subscription tier and report entitlements.
        </Typography>
      </Paper>
    </Box>
  ) : reportsLocked ? (
    <ReportsUpgradeGate onUpgrade={handleUpgrade} />
  ) : (
    <Box id="report-print-zone" sx={{ px: { xs: 1, md: 2 }, py: { xs: 1.5, md: 2.5 } }}>
      {busy && (
        <LinearProgress
          sx={{
            position: "sticky",
            top: { xs: 56, sm: 64 },
            zIndex: 9,
            mb: 1.5,
            height: 4,
            borderRadius: 999,
          }}
        />
      )}

      <GlobalStyles
        styles={{
          "@media print": {
            "@page": { margin: "14mm" },
            "body *": { visibility: "hidden" },
            "#report-print-zone": { position: "absolute", inset: 0, padding: 0 },
            "#report-print-zone > :not(.print-only)": { display: "none !important" },
            "#report-print-zone .print-only, #report-print-zone .print-only *": { visibility: "visible" },
            ".no-print": { display: "none !important" },
            ".print-only": { display: "block !important" },
            ".print-break": { breakAfter: "page" },
            "body": { background: "white" },
          },
        }}
      />

      <Box className="print-only" sx={{ display: "none" }}>
        <Stack spacing={2}>
          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  Enterprise Audit Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reportPacketTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generated: {reportGeneratedAt.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" color="text.secondary">
                  Active Run
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {activeRun ? `#${activeRun.id}` : "None"}
                </Typography>
              </Box>
            </Stack>
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
              <Grid item xs={6} md={3}><StatTile label="Total Assets" value={overview?.total_assets || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Scanned" value={runSummary.scanned_assets || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Coverage" value={`${runSummary.coverage_pct || 0}%`} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Exceptions" value={runSummary.exceptions || 0} color="#b91c1c" /></Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }}>
            <SectionTitle icon={<WarningAmberIcon color="primary" />} title="Overview" subtitle="Audit run status, history, and report context" />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}><StatTile label="Run Coverage" value={`${runSummary.coverage_pct || 0}%`} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Tagged Assets" value={runSummary.tagged_assets || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Audit Scans" value={auditScans.length || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Audit Runs" value={auditRuns.length || 0} /></Grid>
            </Grid>
            <TableContainer sx={{ overflowX: "visible" }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell align="right">Coverage</TableCell>
                    <TableCell align="right">Scanned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditRuns.length ? auditRuns.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>#{run.id}</TableCell>
                      <TableCell>{String(run.status || "-")}</TableCell>
                      <TableCell>{run.started_at ? new Date(run.started_at).toLocaleString() : "-"}</TableCell>
                      <TableCell align="right">{run.summary?.coverage_pct || 0}%</TableCell>
                      <TableCell align="right">{run.summary?.scanned_assets || 0}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No audit runs available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }} className="print-break">
            <SectionTitle icon={<AccountBalanceWalletIcon color="primary" />} title="Financial Audit" subtitle="Depreciation summary and fraud indicators" />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}><StatTile label="Depreciable Assets" value={depreciationRows.length || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Fraud Signals" value={fraudRows.length || 0} color="#b45309" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Gross Asset Value" value={formatMoney(depreciation?.balance_sheet?.gross_asset_value)} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Net Book Value" value={formatMoney(depreciation?.balance_sheet?.net_book_value)} color="#166534" /></Grid>
            </Grid>
            <TableContainer sx={{ mb: 2, overflowX: "visible" }}>
              <Table size="small" sx={{ minWidth: 1120 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Asset UID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Purchase Price</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell align="right">Salvage</TableCell>
                    <TableCell align="right">Life</TableCell>
                    <TableCell align="right">Months Used</TableCell>
                    <TableCell align="right">Monthly Dep.</TableCell>
                    <TableCell align="right">Accumulated Dep.</TableCell>
                    <TableCell align="right">Net Book Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {depreciationRows.length ? depreciationRows.map((row) => (
                    <TableRow key={row.asset_id || row.asset_uid}>
                      <TableCell>{row.asset_uid || "-"}</TableCell>
                      <TableCell>{row.asset_type || "-"}</TableCell>
                      <TableCell align="right">{formatMoney(row.purchase_price)}</TableCell>
                      <TableCell>{row.depreciation_method || "SLM"}</TableCell>
                      <TableCell align="right">{formatMoney(row.salvage_value)}</TableCell>
                      <TableCell align="right">{row.useful_life_months ?? row.useful_life_years ?? "-"}</TableCell>
                      <TableCell align="right">{row.months_used ?? "-"}</TableCell>
                      <TableCell align="right">{formatMoney(row.monthly_depreciation)}</TableCell>
                      <TableCell align="right">{formatMoney(row.accumulated_depreciation)}</TableCell>
                      <TableCell align="right">{formatMoney(row.net_book_value)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">No financial records available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ overflowX: "visible" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Severity</TableCell>
                    <TableCell>Asset UID</TableCell>
                    <TableCell>Signal Type</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fraudRows.length ? fraudRows.map((row, index) => (
                    <TableRow key={`${row.asset_uid || "signal"}-${index}`}>
                      <TableCell>{String(row.severity || "medium").toUpperCase()}</TableCell>
                      <TableCell>{row.asset_uid || "-"}</TableCell>
                      <TableCell>{row.type || "-"}</TableCell>
                      <TableCell>{row.message || "-"}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No fraud indicators were detected</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }} className="print-break">
            <SectionTitle icon={<InventoryIcon color="primary" />} title="Inventory Audit" subtitle="Movement events and scan exceptions" />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}><StatTile label="Exceptions" value={runSummary.exceptions || 0} color="#b91c1c" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Duplicate Scans" value={runSummary.duplicate_scans || 0} color="#b45309" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Geo Breaches" value={runSummary.geofence_breaches || 0} color="#b91c1c" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Movement Events" value={inventoryInsights?.recent_movement_events?.length || 0} /></Grid>
            </Grid>
            <TableContainer sx={{ overflowX: "visible" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Asset</TableCell>
                    <TableCell align="right">Distance (m)</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Scanned By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(inventoryInsights?.recent_movement_events) && inventoryInsights.recent_movement_events.length ? inventoryInsights.recent_movement_events.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</TableCell>
                      <TableCell>{row?.details?.asset_uid || "-"}</TableCell>
                      <TableCell align="right">{row?.details?.movement_distance_meters != null ? Number(row.details.movement_distance_meters).toFixed(2) : "-"}</TableCell>
                      <TableCell>{row?.details?.source || "-"}</TableCell>
                      <TableCell>{row?.actor?.email || "system"}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No movement alerts in the selected period</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }} className="print-break">
            <SectionTitle icon={<AnalyticsIcon color="primary" />} title="SAM License Scope" subtitle="Only scan instances relevant to customer license sizing" />
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button
                className="no-print"
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadSamWorkbook}
              >
                Download SAM Workbook
              </Button>
            </Stack>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}><StatTile label="Managed Devices" value={softwareAudit?.total_devices || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Countable Devices" value={softwareAudit?.matched_devices || 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Relevant Instances" value={softwareAudit?.countable_instances ?? softwareAudit?.matched_installations ?? 0} /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Families Covered" value={softwareRows.length || 0} color="#7c3aed" /></Grid>
            </Grid>
            <TableContainer sx={{ overflowX: "visible" }}>
              <Table size="small" sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Family</TableCell>
                    <TableCell>Counting Basis</TableCell>
                    <TableCell align="right">Countable Devices</TableCell>
                    <TableCell align="right">Relevant Instances</TableCell>
                    <TableCell>Evidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {softwareRows.length ? softwareRows.map((row) => (
                    <TableRow key={`${row.key || row.label || row.name}@@${row.counting_basis}`}>
                      <TableCell>{row.label || row.name || row.key || "-"}</TableCell>
                      <TableCell>{row.counting_basis_label || row.counting_basis || "-"}</TableCell>
                      <TableCell align="right">{row.matched_devices || 0}</TableCell>
                      <TableCell align="right">{row.countable_instances ?? row.matched_installations ?? 0}</TableCell>
                      <TableCell>{(row.counting_basis === "device" ? (row.sample_devices || []) : (row.sample_products || [])).slice(0, 3).map((item) => (typeof item === "string" ? item : item?.hostname || item?.os || item?.name || item?.version || "Sample")).join(", ") || "-"}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No SAM families available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }} className="print-break">
            <SectionTitle
              icon={<DownloadIcon color="primary" />}
              title="License Gap"
              subtitle="Enter customer purchase details and compare them against the scan-relevant installed count."
            />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}><StatTile label="Shortfall" value={samGapSummary.shortfall} color="#b91c1c" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Balanced" value={samGapSummary.balanced} color="#166534" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Surplus" value={samGapSummary.surplus} color="#0369a1" /></Grid>
              <Grid item xs={6} md={3}><StatTile label="Needs Review" value={`${samGapSummary.pending} pending`} color="#7c3aed" /></Grid>
            </Grid>

            {softwareGapRows.length === 0 ? (
              <Alert severity="info">No license gap rows are available yet. Load SAM families first.</Alert>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 1320 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>Vendor</span>
                          <InfoTip title="Vendor associated with the licensable family." />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>SKU / Family</span>
                          <InfoTip title="Canonical family name and the preferred SKU strings used for matching." />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>Purchased Qty</span>
                          <InfoTip title="Quantity purchased by the customer from PO or entitlement records." />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>PO #</span>
                          <InfoTip title="Purchase order number or entitlement reference." />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>PO Date</span>
                          <InfoTip title="The date the purchase order or entitlement was issued." />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <span>Installed Count</span>
                          <InfoTip title="Countable instances from the current scan for this family." />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <span>Gap</span>
                          <InfoTip title="Purchased quantity minus installed count." />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>Reconciliation Status</span>
                          <InfoTip title="Balanced means installed and purchased quantities match. Shortfall means more installations than purchases. Surplus means more purchases than installations. This is not a license-compliance decision." />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>Notes</span>
                          <InfoTip title="Optional customer note or entitlement clarification." />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {softwareGapRows.map((row) => {
                      const gapState = getSamGapState(row.purchased_qty, row.installed_count);
                      return (
                        <TableRow key={row.key}>
                          <TableCell>{row.vendor}</TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row.sku}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(row.canonical_skus || []).join(" · ") || "No canonical SKU list"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Enter purchased qty"
                              value={row.purchased_qty}
                              onChange={(e) => updateSamGapRow(row.key, "purchased_qty", e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="PO #"
                              value={row.po_number}
                              onChange={(e) => updateSamGapRow(row.key, "po_number", e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <TextField
                              size="small"
                              fullWidth
                              type="date"
                              value={row.po_date}
                              onChange={(e) => updateSamGapRow(row.key, "po_date", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </TableCell>
                          <TableCell align="right">{row.installed_count}</TableCell>
                          <TableCell align="right">{gapState.gap == null ? "-" : gapState.gap}</TableCell>
                          <TableCell>
                            <Chip size="small" label={gapState.status} color={gapState.color} variant={gapState.color === "default" ? "outlined" : "filled"} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              placeholder="Optional notes"
                              value={row.notes}
                              onChange={(e) => updateSamGapRow(row.key, "notes", e.target.value)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #dbe6f3", background: "#fff" }} className="print-break">
            <SectionTitle icon={<DownloadIcon color="primary" />} title="Audit Trail" subtitle="Operational history for management review" />
            <TableContainer sx={{ overflowX: "visible" }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length ? history.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</TableCell>
                      <TableCell>{actionLabel(row.action)}</TableCell>
                      <TableCell>{row.actor?.email || "system"}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">No audit trail entries available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </Box>

      <Paper
        className="no-print"
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid #dbe6f3",
          mb: 2,
          background: "radial-gradient(circle at top right, #e0f2fe 0%, #ffffff 40%)",
          }}
        >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
              IT Asset Intelligence & Audit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review inventory, QR audit progress, financial position, software compliance, and cloud spend.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {!activeRun ? (
              <Button
                variant="contained"
                startIcon={<QrCodeScannerIcon />}
                onClick={() => setStartDialogOpen(true)}
              >
                Start Audit Run
              </Button>
            ) : (
              <>
                <Button variant="contained" color="success" onClick={completeAudit}>
                  Complete Audit
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        {activeRun && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }} alignItems={{ sm: "center" }}>
            <Chip size="small" color="success" label={`Active audit #${activeRun.id}`} />
            <Chip size="small" variant="outlined" label={`Coverage ${runSummary.coverage_pct || 0}%`} />
            <Typography variant="caption" color="text.secondary">Custodians complete this audit by scanning their assigned asset QR labels.</Typography>
          </Stack>
        )}

        {error ? <Alert sx={{ mt: 1.5 }} severity="error">{error}</Alert> : null}
        {!error && success ? <Alert sx={{ mt: 1.5 }} severity="success">{success}</Alert> : null}
      </Paper>

      {Number(overview?.missing_owner_email_assets || 0) > 0 ? (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, borderRadius: 2, alignItems: "center" }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`${ROUTES.ASSETS}?view=missing-owner-email`)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Review assets
            </Button>
          }
        >
          {overview.missing_owner_email_assets} asset{overview.missing_owner_email_assets === 1 ? "" : "s"} do not have a custodian email. Add it before starting an audit so the custodian can complete the QR audit.
        </Alert>
      ) : null}

      <Paper className="no-print" sx={{ mb: 2, borderRadius: 2, border: "1px solid #dbe6f3" }}>
        <Tabs
          value={reportTab}
          onChange={(_, value) => {
            setReportTab(value);
            setAuditHistoryPage(0);
            setAssetTrailPage(0);
            setDepPage(0);
            setFraudPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { height: 3, borderRadius: 999 } }}
          sx={{
            minHeight: 54,
            px: 0.5,
            "& .MuiTabs-flexContainer": { gap: 0.5 },
            "& .MuiTab-root": {
              minHeight: 54,
              px: 2.5,
              py: 1.5,
              fontWeight: 800,
              letterSpacing: 0.2,
              textTransform: "uppercase",
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: 999,
            },
          }}
        >
          <Tab label="Audit History" />
          <Tab label="Asset Change Trail" />
          <Tab label="Financial Audit" />
          <Tab label="Inventory Audit" />
          <Tab label="SAM License Scope" />
        </Tabs>
      </Paper>

      {reportTab === 0 ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <SectionTitle
              icon={<DownloadIcon color="primary" />}
              title="Audit History"
              subtitle="Normalized audit-run history with search, status filters, and export actions."
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField
                size="small"
                fullWidth
                label="Search"
                value={auditHistorySearch}
                onChange={(e) => {
                  setAuditHistorySearch(e.target.value);
                  setAuditHistoryPage(0);
                }}
                placeholder="Run ID, status, coverage, scanned count"
              />
              <TextField
                size="small"
                select
                label="Status"
                value={auditHistoryStatus}
                onChange={(e) => {
                  setAuditHistoryStatus(e.target.value);
                  setAuditHistoryPage(0);
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="running">Running</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </TextField>
              <Button variant="outlined" onClick={refreshDashboard} disabled={busy} sx={{ whiteSpace: "nowrap" }}>
                Refresh
              </Button>
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {[["id", "Run"], ["status", "Status"], ["started_at", "Started"], ["coverage_pct", "Coverage"], ["scanned_assets", "Scanned"], ["exceptions", "Exceptions"]].map(([by, label]) => <TableCell key={by} sortDirection={auditRunSort.by === by ? auditRunSort.dir : false}><TableSortLabel active={auditRunSort.by === by} direction={auditRunSort.by === by ? auditRunSort.dir : "asc"} onClick={() => toggleTableSort(setAuditRunSort, by)}>{label}</TableSortLabel></TableCell>)}
                    <TableCell align="right">Export</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedAuditRunRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No audit runs match the selected filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedAuditRunRows.map((run) => (
                      <TableRow key={run.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>#{run.id}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={String(run.status || "-").toUpperCase()}
                            color={String(run.status || "").toLowerCase() === "completed" ? "success" : String(run.status || "").toLowerCase() === "failed" ? "error" : "default"}
                          />
                        </TableCell>
                        <TableCell>{run.started_at ? new Date(run.started_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>{run.summary?.coverage_pct || 0}%</TableCell>
                        <TableCell>{run.summary?.scanned_assets || 0}</TableCell>
                        <TableCell>{run.summary?.exceptions || 0}</TableCell>
                        <TableCell align="right">
                          <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadFullReport({ runId: run.id })}>
                            Asset Audit CSV
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAuditRunRows.length}
              page={auditHistoryPage}
              onPageChange={(_, page) => setAuditHistoryPage(page)}
              rowsPerPage={auditHistoryRowsPerPage}
              onRowsPerPageChange={(e) => {
                setAuditHistoryRowsPerPage(parseInt(e.target.value, 10));
                setAuditHistoryPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ px: 0 }}
            />
          </Paper>
        </Stack>
      ) : null}

      {reportTab === 1 ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 2.25, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <SectionTitle
              icon={<WarningAmberIcon color="warning" />}
              title="Asset Change Trail"
              subtitle="Asset-level changes with search, action filters, pagination, and row detail."
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField
                size="small"
                fullWidth
                label="Search"
                value={assetTrailSearch}
                onChange={(e) => {
                  setAssetTrailSearch(e.target.value);
                  setAssetTrailPage(0);
                }}
                placeholder="Asset UID, action, actor, or details"
              />
              <TextField
                size="small"
                select
                label="Action"
                value={assetTrailAction}
                onChange={(e) => {
                  setAssetTrailAction(e.target.value);
                  setAssetTrailPage(0);
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="ALL">All Actions</MenuItem>
                <MenuItem value="asset_created">Asset Created</MenuItem>
                <MenuItem value="asset_updated">Asset Updated</MenuItem>
                <MenuItem value="asset_custody_transferred">Custody Transferred</MenuItem>
                <MenuItem value="asset_assigned">Asset Assigned</MenuItem>
                <MenuItem value="asset_unassigned">Asset Unassigned</MenuItem>
                <MenuItem value="asset_deleted">Asset Deleted</MenuItem>
                <MenuItem value="asset_bulk_upload">Bulk Upload</MenuItem>
                <MenuItem value="asset_bulk_deleted">Bulk Delete</MenuItem>
                <MenuItem value="asset_qr_scanned">QR Code Scanned</MenuItem>
              </TextField>
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    {[["created_at", "When"], ["action", "Action"], ["asset_uid", "Asset"], ["actor", "By"]].map(([by, label]) => <TableCell key={by} sortDirection={assetTrailSort.by === by ? assetTrailSort.dir : false}><TableSortLabel active={assetTrailSort.by === by} direction={assetTrailSort.by === by ? assetTrailSort.dir : "asc"} onClick={() => toggleTableSort(setAssetTrailSort, by)}>{label}</TableSortLabel></TableCell>)}
                    <TableCell>Summary</TableCell>
                    <TableCell align="right">Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedAuditTrailRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No asset change events match the selected filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedAuditTrailRows.map((row) => {
                      const details = row?.details || {};
                      const assetUid =
                        getTrailDetailValue(row, "asset_uid") ||
                        getTrailDetailValue(row, "assetId") ||
                        getTrailDetailValue(row, "asset_id") ||
                        "-";
                      const summary = details?.change_summary || details?.message || details?.reason || details?.action || "-";
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => setSelectedTrailRow(row)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={actionLabel(row.action)} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{assetUid}</TableCell>
                          <TableCell>{row.actor?.email || "system"}</TableCell>
                          <TableCell sx={{ maxWidth: 340 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {String(summary)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrailRow(row);
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAuditTrailRows.length}
              page={assetTrailPage}
              onPageChange={(_, page) => setAssetTrailPage(page)}
              rowsPerPage={assetTrailRowsPerPage}
              onRowsPerPageChange={(e) => {
                setAssetTrailRowsPerPage(parseInt(e.target.value, 10));
                setAssetTrailPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ px: 0 }}
            />
          </Paper>

          <Drawer
            anchor="right"
            open={Boolean(selectedTrailRow)}
            onClose={() => setSelectedTrailRow(null)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 0 } }}
          >
            <Box sx={{ p: 2.5, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Asset Change Detail
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Plain-language summary of what changed for this asset.
                  </Typography>
                </Box>
                <IconButton onClick={() => setSelectedTrailRow(null)}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {selectedTrailRow ? (
                <Stack spacing={2}>
                  <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={actionLabel(selectedTrailRow.action)} />
                        <Chip size="small" variant="outlined" label={selectedTrailRow.actor?.email || "system"} />
                      </Stack>
                      <Typography variant="body2">
                        <strong>When:</strong> {selectedTrailRow.created_at ? new Date(selectedTrailRow.created_at).toLocaleString() : "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Asset UID:</strong> {getTrailDetailValue(selectedTrailRow, "asset_uid") || getTrailDetailValue(selectedTrailRow, "assetId") || getTrailDetailValue(selectedTrailRow, "asset_id") || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This entry is part of the asset's audit trail and records the event below in a human-readable form.
                      </Typography>
                    </Stack>
                  </Paper>
                  <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      What changed
                    </Typography>
                    <Stack spacing={1}>
                      {buildTrailHighlights(selectedTrailRow).length ? (
                        buildTrailHighlights(selectedTrailRow).map((item, index) => (
                          <Paper key={`${item.label}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                              {item.value}
                            </Typography>
                          </Paper>
                        ))
                      ) : (
                        <Alert severity="info">No extra change details were recorded for this event.</Alert>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              ) : null}
            </Box>
          </Drawer>
        </Stack>
      ) : null}

      {false ? (
        <Stack spacing={2}>
          {activeRun ? (
            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Current Audit Run</Typography>
                  <Typography variant="body2" color="text.secondary">Live scan activity, audit trail, and current run status.</Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Button variant="outlined" onClick={refreshDashboard} disabled={busy}>Refresh</Button>
                </Stack>
              </Stack>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6} md={3}><StatTile label="Total Assets" value={overview?.total_assets || 0} /></Grid>
                <Grid item xs={6} md={3}><StatTile label="Scanned" value={runSummary.scanned_assets || 0} /></Grid>
                <Grid item xs={6} md={3}><StatTile label="Coverage" value={`${runSummary.coverage_pct || 0}%`} /></Grid>
                <Grid item xs={6} md={3}><StatTile label="Exceptions" value={runSummary.exceptions || 0} color="#b91c1c" /></Grid>
              </Grid>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>No audit is running right now. Start a new audit to see live status here.</Alert>
          )}

          {activeRun ? (
            <Paper sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}>
              <SectionTitle icon={<WarningAmberIcon color="warning" />} title="Audit Activity" subtitle="Each scan is recorded automatically, with full detail available for review and exception handling" />
              {auditScansBusy ? <LinearProgress sx={{ mb: 1 }} /> : null}
              {auditScans.length === 0 ? (
                <Alert severity="info">No scans found for the active audit run yet.</Alert>
              ) : (
                <Stack spacing={1.25}>
                  {auditScans.map((row) => {
                    const status = String(row.approval_status || "approved").toLowerCase();
                    const statusLabel = status === "approved" ? "Auto-Approved" : status === "review" ? "In Review" : status === "rejected" ? "Rejected" : "Auto-Approved";
                    const hasGeoLocation = row.asset?.geofence_locked && row.asset?.geofence_center_latitude != null && row.asset?.geofence_center_longitude != null && row.asset?.geofence_radius_meters != null;
                    const scanMapsUrl = buildGoogleMapsUrl(row.latitude, row.longitude);
                    const assetMapsUrl = hasGeoLocation ? buildGoogleMapsUrl(row.asset.geofence_center_latitude, row.asset.geofence_center_longitude) : "";
                    return (
                      <Accordion key={row.id} disableGutters sx={{ border: "1px solid #e2e8f0", borderRadius: 1.75, overflow: "hidden", boxShadow: "0 1px 0 rgba(15,23,42,0.03)" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "flex-start", md: "center" }} sx={{ width: "100%", pr: 1 }}>
                            <Typography sx={{ fontWeight: 700 }}>{row.asset_uid || row?.asset?.asset_uid || "-"}</Typography>
                            <Chip size="small" label={statusLabel} clickable={status === "review"} onClick={status === "review" ? () => openResolveReview(row.id) : undefined} sx={{ fontWeight: 700, border: "1px solid", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", cursor: status === "review" ? "pointer" : "default", ...(status === "approved" ? { bgcolor: "#e8fff0", color: "#166534", borderColor: "#86efac" } : status === "review" ? { bgcolor: "#fff7ed", color: "#9a3412", borderColor: "#fdba74" } : { bgcolor: "#fff1f2", color: "#991b1b", borderColor: "#fca5a5" }) }} />
                            <Chip size="small" color={row.is_geofence_breach ? "error" : "success"} variant="outlined" label={row.is_geofence_breach ? "Boundary Exception" : "Within Expected Boundary"} />
                            <Typography variant="body2" color="text.secondary">{row.scanned_at ? new Date(row.scanned_at).toLocaleString() : "-"} {row.source ? `• ${row.source}` : ""}</Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: "#fafcff", px: 2.5, py: 2.25 }}>
                          <Grid container spacing={2.5}>
                            <Grid item xs={12} md={4}><Stack spacing={0.85}><Typography variant="body2"><strong>Asset:</strong> {row.asset?.asset_uid || row.asset_uid || "-"}</Typography><Typography variant="body2"><strong>Type:</strong> {row.asset?.asset_type || "-"}</Typography><Typography variant="body2"><strong>Status:</strong> {row.asset?.asset_status || "-"}</Typography><Typography variant="body2"><strong>Owner:</strong> {row.asset?.owner_name || "-"}</Typography><Typography variant="body2"><strong>Custodian Name:</strong> {row.asset?.asset_custodian || "-"}</Typography><Typography variant="body2"><strong>Custodian Email:</strong> {row.asset?.asset_custodian_email || "-"}</Typography><Typography variant="body2"><strong>Custodian Contact:</strong> {row.asset?.asset_custodian_contact || "-"}</Typography><Typography variant="body2"><strong>Geofence Center:</strong> {hasGeoLocation ? <Box component="a" href={assetMapsUrl} target="_blank" rel="noreferrer" sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}>Open on Google Maps</Box> : "Geolocation not configured"}</Typography></Stack></Grid>
                            <Grid item xs={12} md={4}><Stack spacing={0.85}><Typography variant="body2"><strong>Scan Location:</strong> {scanMapsUrl ? <Box component="a" href={scanMapsUrl} target="_blank" rel="noreferrer" sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}>Open on Google Maps</Box> : "-"}</Typography><Typography variant="body2"><strong>Boundary Distance:</strong> {row.distance_from_geofence_meters != null ? Number(row.distance_from_geofence_meters).toFixed(2) : "-"}</Typography><Typography variant="body2"><strong>Approved on:</strong> {row.approved_at ? new Date(row.approved_at).toLocaleString() : "-"}</Typography></Stack></Grid>
                            <Grid item xs={12} md={4}><Stack spacing={0.85}><Typography variant="body2"><strong>Review note:</strong> {row.rejection_reason || "-"}</Typography><Typography variant="body2"><strong>Source:</strong> {row.source || "-"}</Typography><Typography variant="body2"><strong>Boundary Check:</strong> {row.is_geofence_breach ? "Exception detected" : "Within range"}</Typography></Stack></Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          ) : null}

          <Paper sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <SectionTitle icon={<DownloadIcon color="primary" />} title="Audit Trail and Run History" subtitle="Enterprise auditability for changes and run exports" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Asset Change History</Typography>
            <TableContainer sx={{ mb: 2, overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead><TableRow><TableCell>When</TableCell><TableCell>Action</TableCell><TableCell>By</TableCell></TableRow></TableHead>
                <TableBody>{history.length === 0 ? <TableRow><TableCell colSpan={3} align="center">No audit events yet</TableCell></TableRow> : history.map((row) => (<TableRow key={row.id} hover><TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</TableCell><TableCell><Chip size="small" label={actionLabel(row.action)} /></TableCell><TableCell>{row.actor?.email || "system"}</TableCell></TableRow>))}</TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Audit Run History</Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead><TableRow><TableCell>Run</TableCell><TableCell>Status</TableCell><TableCell>Started</TableCell><TableCell>Coverage</TableCell><TableCell>Scanned</TableCell><TableCell>Download</TableCell></TableRow></TableHead>
                <TableBody>{auditRuns.length === 0 ? <TableRow><TableCell colSpan={6} align="center">No audit runs yet</TableCell></TableRow> : auditRuns.map((run) => (<TableRow key={run.id} hover><TableCell>#{run.id}</TableCell><TableCell><Chip size="small" label={run.status} /></TableCell><TableCell>{run.started_at ? new Date(run.started_at).toLocaleString() : "-"}</TableCell><TableCell>{run.summary?.coverage_pct || 0}%</TableCell><TableCell>{run.summary?.scanned_assets || 0}</TableCell><TableCell><Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadFullReport({ runId: run.id })}>Asset Audit CSV</Button></TableCell></TableRow>))}</TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      ) : null}

      {reportTab === 2 ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <SectionTitle icon={<AccountBalanceWalletIcon color="primary" />} title="Financial Audit" subtitle="Depreciation and fraud indicators" />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField size="small" fullWidth label="Search" value={financeSearch} onChange={(e) => setFinanceSearch(e.target.value)} placeholder="Asset UID, type, or fraud message" />
              <TextField size="small" select label="Asset Type" value={financeAssetType} onChange={(e) => setFinanceAssetType(e.target.value)} sx={{ minWidth: 180 }}><MenuItem value="ALL">All Types</MenuItem><MenuItem value="DEVICE">Device</MenuItem><MenuItem value="SOFTWARE">Software</MenuItem><MenuItem value="LICENSE">License</MenuItem><MenuItem value="OTHER">Other</MenuItem></TextField>
              <TextField size="small" select label="Fraud Severity" value={fraudSeverity} onChange={(e) => setFraudSeverity(e.target.value)} sx={{ minWidth: 180 }}><MenuItem value="ALL">All Severities</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></TextField>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
                mb: 2,
              }}
            >
              <StatTile label="Depreciable Assets" value={depreciationRows.length || 0} />
              <StatTile label="Gross Asset Value" value={formatMoney(depreciation?.balance_sheet?.gross_asset_value)} />
              <StatTile label="Accumulated Depreciation" value={formatMoney(depreciation?.balance_sheet?.accumulated_depreciation)} color="#b45309" />
              <StatTile label="Net Book Value" value={formatMoney(depreciation?.balance_sheet?.net_book_value)} color="#166534" />
              <StatTile
                label="Fraud Signals"
                value={fraudSignals?.summary?.total_signals ?? normalizedFraudRows.length ?? 0}
                color="#b45309"
                onClick={() => setFraudTableOpen((open) => !open)}
                selected={fraudTableOpen}
              />
            </Box>
            {depreciationRows.length === 0 ? <Alert severity="info" sx={{ mb: 2 }}>No depreciable assets found for the selected filters.</Alert> : <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center" }}>Balance Sheet and Depreciation<InfoTip title="Financial view shows per-asset depreciation values, floor-limit salvage value, and portfolio rollup as of today." /></Typography>
              <TableContainer sx={{ mb: 2, overflowX: "auto" }}><Table size="small" sx={{ minWidth: 1120 }}><TableHead><TableRow>{[["asset_uid", "Asset UID", "left"], ["asset_type", "Type", "left"], ["purchase_price", "Purchase Price", "right"], ["depreciation_method", "Method", "center"], ["salvage_value", "Salvage", "right"], ["useful_life_months", "Useful Life", "right"], ["depreciation_start_mode", "Start Mode", "center"], ["months_used", "Months Used", "right"], ["monthly_depreciation", "Monthly Depreciation", "right"], ["accumulated_depreciation", "Accumulated Depreciation", "right"], ["net_book_value", "Net Book Value", "right"]].map(([by, label, align]) => <TableCell key={by} align={align} sortDirection={depreciationSort.by === by ? depreciationSort.dir : false}><TableSortLabel active={depreciationSort.by === by} direction={depreciationSort.by === by ? depreciationSort.dir : "asc"} onClick={() => toggleTableSort(setDepreciationSort, by)}>{label}</TableSortLabel></TableCell>)}</TableRow></TableHead><TableBody>{pagedDepreciationRows.map((a) => (<TableRow key={a.asset_id} hover><TableCell>{a.asset_uid || "-"}</TableCell><TableCell>{a.asset_type || "-"}</TableCell><TableCell align="right">{formatMoney(a.purchase_price)}</TableCell><TableCell align="center">{a.depreciation_method || "SLM"}</TableCell><TableCell align="right">{formatMoney(a.salvage_value)}</TableCell><TableCell align="right">{a.useful_life_months ?? a.useful_life_years}</TableCell><TableCell align="center">{a.depreciation_start_mode === "NEXT_MONTH" ? "Next month" : "Purchase month"}</TableCell><TableCell align="right">{a.months_used}</TableCell><TableCell align="right">{formatMoney(a.monthly_depreciation)}</TableCell><TableCell align="right">{formatMoney(a.accumulated_depreciation)}</TableCell><TableCell align="right">{formatMoney(a.net_book_value)}</TableCell></TableRow>))}</TableBody></Table></TableContainer>
              <TablePagination component="div" count={filteredDepreciationRows.length} page={depPage} onPageChange={(_, page) => setDepPage(page)} rowsPerPage={depRowsPerPage} onRowsPerPageChange={(e) => { setDepRowsPerPage(parseInt(e.target.value, 10)); setDepPage(0); }} rowsPerPageOptions={[10, 25, 50]} sx={{ px: 0 }} />
            </>}
            <Dialog
              open={fraudTableOpen}
              onClose={() => setFraudTableOpen(false)}
              fullWidth
              maxWidth="lg"
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
                Audit Fraud Signals
              </DialogTitle>
              <DialogContent dividers sx={{ pt: 2 }}>
                {normalizedFraudRows.length === 0 ? (
                  <Alert severity="info">No fraud heuristics triggered currently.</Alert>
                ) : (
                  <>
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Severity</TableCell>
                            <TableCell>Asset UID</TableCell>
                            <TableCell>Signal Type</TableCell>
                            <TableCell>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pagedFraudRows.map((s) => (
                            <TableRow key={s.id} hover>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={s.severity === "high" ? "error" : s.severity === "medium" ? "warning" : "default"}
                                  label={String(s.severity || "medium").toUpperCase()}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{s.asset_uid || "-"}</TableCell>
                              <TableCell>{s.type || "-"}</TableCell>
                              <TableCell>{s.message}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={filteredFraudRows.length}
                      page={fraudPage}
                      onPageChange={(_, page) => setFraudPage(page)}
                      rowsPerPage={fraudRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setFraudRowsPerPage(parseInt(e.target.value, 10));
                        setFraudPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      sx={{ px: 0, mt: 1 }}
                    />
                  </>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="outlined" onClick={() => setFraudTableOpen(false)}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        </Stack>
      ) : null}

      {reportTab === 3 ? (
        <Paper sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}>
          <SectionTitle icon={<InventoryIcon color="primary" />} title="Inventory Audit" subtitle="Geo-fencing and movement anomalies from live scans" />
          <Grid container spacing={2} sx={{ mb: 2 }}><Grid item xs={12} sm={6} md={3}><StatTile label="Coverage" value={`${runSummary.coverage_pct || 0}%`} /></Grid><Grid item xs={12} sm={6} md={3}><StatTile label="Exceptions" value={runSummary.exceptions || 0} color="#b91c1c" /></Grid><Grid item xs={12} sm={6} md={3}><StatTile label="Duplicate Scans" value={runSummary.duplicate_scans || 0} color="#b45309" /></Grid><Grid item xs={12} sm={6} md={3}><StatTile label="Geo Breaches" value={runSummary.geofence_breaches || 0} color="#b91c1c" /></Grid></Grid>
          {Array.isArray(inventoryInsights?.recent_movement_events) && inventoryInsights.recent_movement_events.length ? <TableContainer sx={{ overflowX: "auto" }}><Table size="small" sx={{ minWidth: 900 }}><TableHead><TableRow><TableCell>When</TableCell><TableCell>Asset</TableCell><TableCell>Distance (m)</TableCell><TableCell>Source</TableCell><TableCell>Scanned By</TableCell></TableRow></TableHead><TableBody>{inventoryInsights.recent_movement_events.map((row) => (<TableRow key={row.id} hover><TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</TableCell><TableCell>{row?.details?.asset_uid || "-"}</TableCell><TableCell>{row?.details?.movement_distance_meters ? Number(row.details.movement_distance_meters).toFixed(2) : "-"}</TableCell><TableCell>{row?.details?.source || "-"}</TableCell><TableCell>{row?.actor?.email || "system"}</TableCell></TableRow>))}</TableBody></Table></TableContainer> : <Alert severity="info">No movement alerts in the selected period.</Alert>}
        </Paper>
      ) : null}

      {reportTab === 4 ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <SectionTitle
              icon={<AnalyticsIcon color="primary" />}
              title="SAM License Scope"
              subtitle="Count only the software families that affect license sizing."
            />
            <Alert severity="info" sx={{ mb: 2 }}>
              This view excludes generic inventory noise. Device-based families count devices, application families count installations, and the totals here are the ones used for license planning.
            </Alert>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}><StatTile label="Managed Devices" value={softwareAudit?.total_devices || 0} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatTile label="Countable Devices" value={softwareAudit?.matched_devices || 0} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatTile label="Relevant Instances" value={(softwareAudit?.countable_instances ?? softwareAudit?.matched_installations) || 0} /></Grid>
              <Grid item xs={12} sm={6} md={3}><StatTile label="Families Covered" value={softwareRows.length || 0} color="#7c3aed" /></Grid>
            </Grid>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField
                size="small"
                fullWidth
                label="Search"
                value={softwareSearch}
                onChange={(e) => {
                  setSoftwareSearch(e.target.value);
                  setSoftwarePage(0);
                }}
                placeholder="Family, product, or device"
              />
              <TextField
                size="small"
                select
                label="Counting Basis"
                value={softwareCoverage}
                onChange={(e) => {
                  setSoftwareCoverage(e.target.value);
                  setSoftwarePage(0);
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="ALL">All Families</MenuItem>
                <MenuItem value="device">Device-based</MenuItem>
                <MenuItem value="installation">Installation-based</MenuItem>
              </TextField>
              <TextField
                size="small"
                select
                label="Sort By"
                value={softwareSort}
                onChange={(e) => setSoftwareSort(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="installations">Relevant Instances</MenuItem>
                <MenuItem value="name">Family Name</MenuItem>
                <MenuItem value="devices">Countable Devices</MenuItem>
              </TextField>
            </Stack>

            {softwareSortedRows.length === 0 ? (
              <Alert severity="info">No SAM families match the selected filters.</Alert>
            ) : (
              <>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <span>Family</span>
                            <InfoTip title="Customer-facing product family that groups licensable installs into one reportable line." />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <span>Counting Basis</span>
                            <InfoTip title="Device-based rows count endpoints. Installation-based rows count licensable installs." />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <span>Countable Devices</span>
                            <InfoTip title="Managed devices that matched the family rule set." />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <span>Relevant Instances</span>
                            <InfoTip title="Countable scan results used for license sizing." />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <span>Sample Evidence</span>
                            <InfoTip title="Example hostnames or product names that matched the selected family rule." />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedSoftwareRows.map((row) => (
                        <TableRow
                          hover
                          key={`${row.key || row.label || row.name}@@${row.counting_basis}`}
                          onClick={() => {
                            setSelectedSoftwareRow(row);
                            setSoftwareDetailOpen(true);
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ fontWeight: 700 }}>{row.label || row.name || row.key || "-"}</TableCell>
                          <TableCell>{row.counting_basis_label || row.counting_basis || "-"}</TableCell>
                          <TableCell align="right">{row.matched_devices || 0}</TableCell>
                          <TableCell align="right">{row.countable_instances ?? row.matched_installations ?? 0}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              {(row.counting_basis === "device" ? (row.sample_devices || []) : (row.sample_products || [])).slice(0, 3).map((item) => (
                                <Chip
                                  key={typeof item === "string" ? item : `${item?.hostname || item?.os || item?.name || item?.version || "sample"}`}
                                  size="small"
                                  label={typeof item === "string" ? item : item?.hostname || item?.os || item?.name || item?.version || "Sample"}
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={softwareSortedRows.length}
                  page={softwarePage}
                  onPageChange={(_, page) => setSoftwarePage(page)}
                  rowsPerPage={softwareRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setSoftwareRowsPerPage(parseInt(e.target.value, 10));
                    setSoftwarePage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  sx={{ px: 0, mt: 1 }}
                />
              </>
            )}
          </Paper>
        </Stack>
      ) : null}

      <Drawer
        anchor="right"
        open={softwareDetailOpen}
        onClose={() => setSoftwareDetailOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 2 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              SAM Family Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              License-sizing basis, countable instances, and sample evidence.
            </Typography>
          </Box>
          <IconButton onClick={() => setSoftwareDetailOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {selectedSoftwareRow ? (
          <Stack spacing={2}>
            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                {selectedSoftwareRow.label || selectedSoftwareRow.name || selectedSoftwareRow.key || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Counting basis: {selectedSoftwareRow.counting_basis_label || selectedSoftwareRow.counting_basis || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Countable devices: {selectedSoftwareRow.matched_devices || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Relevant instances: {selectedSoftwareRow.countable_instances ?? selectedSoftwareRow.matched_installations ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Family key: {selectedSoftwareRow.key || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                License metric: {selectedSoftwareRow.license_metric || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Count rule: {selectedSoftwareRow.count_rule || "-"}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {(selectedSoftwareRow.preferred_skus || []).slice(0, 5).map((sku) => (
                  <Chip key={sku} size="small" label={sku} variant="outlined" />
                ))}
              </Stack>
              <Chip
                size="small"
                label={selectedSoftwareRow.counting_basis_label || selectedSoftwareRow.counting_basis || "Scoped"}
                color="primary"
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" sx={{ mt: 1.25 }}>
                {selectedSoftwareRow.counting_basis === "device"
                  ? "Each matched device contributes one license-relevant count."
                  : "Each matched installation contributes one license-relevant count."}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Sample evidence
              </Typography>
              <Stack spacing={1}>
                {(selectedSoftwareRow.counting_basis === "device" ? (selectedSoftwareRow.sample_devices || []) : (selectedSoftwareRow.sample_products || [])).length ? (selectedSoftwareRow.counting_basis === "device" ? selectedSoftwareRow.sample_devices : selectedSoftwareRow.sample_products).map((item, index) => (
                  <Paper key={`${selectedSoftwareRow.key || selectedSoftwareRow.label || selectedSoftwareRow.name}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {typeof item === "string" ? item : item?.hostname || item?.os || item?.name || item?.version || "Sample"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typeof item === "string" ? "Sample record" : [item?.manufacturer, item?.model, item?.version].filter(Boolean).join(" • ") || "Sample record"}
                    </Typography>
                  </Paper>
                )) : (
                  <Alert severity="info">No sample evidence is available for this family yet.</Alert>
                )}
              </Stack>
            </Paper>
          </Stack>
        ) : (
          <Alert severity="info">Select a family to see license-sizing details.</Alert>
        )}
      </Drawer>

      <Dialog open={startDialogOpen} onClose={() => setStartDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Start Asset Audit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enterprise device current location is captured automatically in background and used as geofence center.
          </Typography>
          <Alert severity="info" sx={{ mb: 1.25 }}>
            Browser location permission is required to start audit.
          </Alert>
          <Stack spacing={1.2}>
            {startAuditLocating ? <LinearProgress sx={{ borderRadius: 999, height: 6 }} /> : null}
            {startAuditLocationStatus === "ready" ? (
              <Alert severity="success">{startAuditLocationMessage || "Current location captured successfully."}</Alert>
            ) : null}
            {startAuditLocationStatus === "error" ? (
              <Alert severity="error">{startAuditLocationMessage || "Unable to capture current location."}</Alert>
            ) : null}
            <TextField label="Radius (meters)" value={geoRadiusMeters} onChange={(e) => setGeoRadiusMeters(e.target.value)} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => {
                  void captureStartAuditLocation().catch((err) => {
                    setStartAuditLocationStatus("error");
                    setStartAuditLocationMessage(err?.message || "Unable to capture current location.");
                  });
                }}
                disabled={startAuditLocating}
              >
                {startAuditLocating ? "Refreshing Location..." : "Refresh Current Location"}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDialogOpen(false)}>Cancel</Button>
          <Button onClick={startAudit} variant="contained" disabled={busy || startAuditLocating}>Start</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record Asset Scan</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="info">
              Current device location is mandatory for every scan. Browser will request location permission before save.
            </Alert>
            <Select
              size="small"
              value={scanSource}
              onChange={(e) => {
                setScanSource(e.target.value);
                stopScanner();
                setScannerError("");
                setScannedAssetPreview(null);
              }}
            >
              <MenuItem value="manual">Manual Scan</MenuItem>
              <MenuItem value="camera_qr">QR Camera Scan</MenuItem>
            </Select>
            <TextField
              fullWidth
              label="Asset UID / QR Value"
              placeholder="AST-1-0001"
              value={scanAssetUid}
              onChange={(e) => setScanAssetUid(e.target.value)}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="outlined" onClick={fetchManualAssetDetails} disabled={!scanAssetUid.trim() || scanLookupBusy}>
                {scanLookupBusy ? "Loading Details..." : "Fetch Asset Details"}
              </Button>
              {scanSource === "camera_qr" ? (
                <Button variant="contained" onClick={openCameraScanner}>
                  Open Camera Scanner
                </Button>
              ) : null}
            </Stack>


            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5, bgcolor: "#f8fafc" }}>
              <Stack spacing={0.75}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Optional photos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Attach up to 3 photos for admin review. Images are retained for 6 months.
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AddPhotoAlternateIcon />}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  Add photos
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setScanPhotos(Array.from(e.target.files || []).slice(0, 3))}
                  />
                </Button>
                {scanPhotos.length ? (
                  <Typography variant="body2" color="text.secondary">
                    {scanPhotos.length} photo{scanPhotos.length > 1 ? "s" : ""} selected
                  </Typography>
                ) : null}
              </Stack>
            </Box>

            {scannerError ? <Alert severity="warning">{scannerError}</Alert> : null}

            {scannerOpen ? (
              <Box sx={{ border: "1px solid #dbe6f3", borderRadius: 2, p: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Point the camera at the asset QR code. The scan will save automatically.
                </Typography>
                <Box
                  component="video"
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  sx={{ width: "100%", borderRadius: 1, backgroundColor: "#000" }}
                />
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                  <Button size="small" onClick={stopScanner}>Stop Camera</Button>
                </Stack>
              </Box>
            ) : null}

            {scannedAssetPreview ? (
              <Paper sx={{ p: 1.25, border: "1px solid #e2e8f0", borderRadius: 1.5, backgroundColor: "#f8fafc" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Scanned Asset Preview</Typography>
                <Typography variant="body2">Asset UID: <strong>{scannedAssetPreview.asset_uid || "-"}</strong></Typography>
                <Typography variant="body2">Type: <strong>{scannedAssetPreview.asset_type || "-"}</strong></Typography>
                <Typography variant="body2">Owner: <strong>{scannedAssetPreview.owner_name || "-"}</strong></Typography>
                <Typography variant="body2">Custodian Name: <strong>{scannedAssetPreview.asset_custodian || "-"}</strong></Typography>
                <Typography variant="body2">Custodian Email: <strong>{scannedAssetPreview.asset_custodian_email || "-"}</strong></Typography>
                <Typography variant="body2">Custodian Contact: <strong>{scannedAssetPreview.asset_custodian_contact || "-"}</strong></Typography>
                <Typography variant="body2">Status: <strong>{scannedAssetPreview.asset_status || "-"}</strong></Typography>
                <Typography variant="body2">Location: <strong>{scannedAssetPreview.location || "-"}</strong></Typography>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              stopScanner();
              setScanDialogOpen(false);
              setScanPhotos([]);
            }}
          >
            Close
          </Button>
          <Button onClick={() => addScan()} variant="contained" disabled={busy || !scanAssetUid.trim()}>
            Save Scan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{reviewDialogMode === "resolve" ? "Resolve Review" : "Flag Scan for Review"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity="info">
              {reviewDialogMode === "resolve"
                ? "Mark this scan as resolved to return it to the approved state."
                : "This keeps the scan recorded, while routing it to an admin review queue with a required note."}
            </Alert>
            {reviewDialogMode === "flag" ? (
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label="Review note"
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder="Example: Asset photo does not match, or location looks inconsistent."
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submitReviewReason} variant="contained" disabled={busy}>
            {reviewDialogMode === "resolve" ? "Mark as Resolved" : "Flag for Review"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


